import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { ContentBlockType } from '@onesource/schemas';
import { getMediaUrl, useCompleteBlock, useGetLesson, useGetLessons, useGetSpace } from '@onesource/services';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { MdDownload, MdPictureAsPdf } from 'react-icons/md';
import { Document, Page, pdfjs } from 'react-pdf';

import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';
import { downloadFileFromUrl } from '#/utils/download-file';
import { isIOS } from '#/utils/open-url';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set PDF.js worker for react-pdf v10+ (using CDN for compatibility with Vite)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * PDF Content Route
 * Path: /community-hub/spaces/:id/modules/:moduleId/lessons/:lessonId/pdf/:contentId
 * Shows a PDF content block with viewer/download
 */
export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/pdf/$contentId',
)({
  component: PdfContentRoute,
});

function PdfContentRoute() {
  const { id: spaceId, lessonId, contentId, moduleId } = Route.useParams();

  // PDF viewer state
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.2);
  const [pdfLoading, setPdfLoading] = useState<boolean>(true);
  const [useFallback, setUseFallback] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  // Data fetching hooks
  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);
  const { data: module } = useGetSpace(moduleId);
  const { data: lessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  // PDF is a view block: opening it completes it.
  const trackedRef = useRef(false);
  const { mutate: completeBlock } = useCompleteBlock();
  const pdfBlock = lesson?.contentBlocks?.find(
    block => block.id === contentId && block.blockType === ContentBlockType.PDF,
  );
  useEffect(() => {
    if (!trackedRef.current && pdfBlock) {
      trackedRef.current = true;
      completeBlock(contentId);
    }
  }, [contentId, pdfBlock, completeBlock]);

  // PDF navigation and zoom handlers
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  // Fallback to iframe if react-pdf fails (e.g., CORS issues with external PDFs)
  const onDocumentLoadError = () => {
    setPdfLoading(false);
    setUseFallback(true);
  };

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));

  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load content: {lessonError.message}</Alert>
      </Box>
    );
  }

  if (!pdfBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">PDF not found</Alert>
      </Box>
    );
  }

  // The react-pdf viewer crashes / fails to load on iOS Safari (pdfjs needs
  // Promise.withResolvers, missing on older iPads), so iPad/iPhone users get a
  // download-only experience instead of the inline viewer.
  const isIosDevice = isIOS();

  // Download directly via blob so the CDN URL stays hidden and the file downloads
  // instead of opening in a new tab (iPad/Safari ignores the cross-origin download attr).
  const handleDownload = async () => {
    const url = getMediaUrl(pdfBlock.pdfUrl);
    if (!url) {
      return;
    }
    setIsDownloading(true);
    try {
      await downloadFileFromUrl(url, pdfBlock.pdfFilename || 'Document.pdf');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box>
      <LessonBreadcrumbs
        spaceId={spaceId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel="Document"
      />

      {/* PDF Viewer/Download Section */}
      <Box>
        {pdfBlock.pdfUrl ? (
          <Box display="flex" gap={3}>
            {/* PDF Controls - only show for react-pdf viewer (never on iOS) */}
            {!isIosDevice && !useFallback && !pdfLoading && (
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mb: 2,
                  p: 2,
                  backgroundColor: 'grey.100',
                  borderRadius: 2,
                  maxWidth: 900,
                }}
              >
                <IconButton onClick={goToPrevPage} disabled={pageNumber <= 1}>
                  <NavigateBeforeIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'center' }}>
                  Page {pageNumber} of {numPages || pdfBlock.pdfPageCount || '?'}
                </Typography>
                <IconButton onClick={goToNextPage} disabled={pageNumber >= numPages}>
                  <NavigateNextIcon />
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <IconButton onClick={zoomOut} disabled={scale <= 0.5}>
                  <ZoomOutIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
                  {Math.round(scale * 100)}%
                </Typography>
                <IconButton onClick={zoomIn} disabled={scale >= 2.5}>
                  <ZoomInIcon />
                </IconButton>
              </Box>
            )}

            {/* PDF Viewer - react-pdf or iframe fallback. Skipped on iOS (download only). */}
            {!isIosDevice &&
              (useFallback ? (
                // Fallback to iframe for external PDFs with CORS issues
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    width: '100%',
                    maxWidth: 900,
                    height: 750,
                    borderRadius: '15px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <iframe
                    src={`${getMediaUrl(pdfBlock.pdfUrl)}#view=FitH`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    title={pdfBlock.title || 'PDF Viewer'}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'auto',
                    maxHeight: 'calc(100vh - 400px)',
                    minHeight: 750,
                    width: '100%',
                    maxWidth: 900,
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'center',
                    backgroundColor: 'grey.200',
                  }}
                >
                  {pdfLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}
                  <Document
                    file={pdfBlock.pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={null}
                  >
                    <Page pageNumber={pageNumber} scale={scale} renderTextLayer renderAnnotationLayer />
                  </Document>
                </Box>
              ))}

            {/* Download Card */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                gap: { xs: 2, md: 3 },
                p: { xs: 3, md: 3.5 },
                bgcolor: 'white',
                borderRadius: '15px',
                height: 'fit-content',
                maxWidth: { xs: '100%', md: 235 },
                mb: 3,
              }}
            >
              {pdfBlock.pdfBannerUrl && getMediaUrl(pdfBlock.pdfBannerUrl) ? (
                <Box
                  component="img"
                  src={getMediaUrl(pdfBlock.pdfBannerUrl)}
                  alt={pdfBlock.title || 'Document'}
                  sx={{
                    width: { xs: '66px', md: '90px' },
                    height: { xs: '81px', md: '109px' },
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <MdPictureAsPdf size={48} color="#d32f2f" />
              )}{' '}
              <Box
                sx={{
                  gap: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: { xs: '12px', md: '14px' } }}>
                  {pdfBlock.pdfFilename || 'Document.pdf'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <MdDownload />}
                  onClick={handleDownload}
                  disabled={isDownloading}
                  size="small"
                >
                  {isDownloading ? 'Downloading…' : 'Download'}
                </Button>
                {pdfBlock.pdfPageCount && (
                  <Typography variant="caption" color="text.secondary">
                    {pdfBlock.pdfPageCount} pages
                    {pdfBlock.pdfFileSize && ` • ${Math.round(pdfBlock.pdfFileSize / 1024)} KB`}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: 400,
              bgcolor: 'grey.100',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              maxWidth: 900,
            }}
          >
            <MdPictureAsPdf size={64} color="#999" />
            <Typography color="text.secondary">PDF not available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
